import { memo } from "react";
import { useHover } from "@mantine/hooks";
import numeral from "numeral";

import {
  Center,
  Image,
  Overlay,
  RingProgress,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton
} from "@mantine/core";

import {
  TbAlertTriangle,
  TbFile,
  TbFileExcel,
  TbFileTypePdf,
  TbPresentation,
  TbSvg,
  TbX
} from "react-icons/tb";

import classes from "./styles.module.css";

export const getAttachmentIcon = (type: string) => {
  if (type.indexOf("pdf") > -1)
    return <TbFileTypePdf size={20} color="var(--mantine-color-red-5)" />;
  if (
    type.indexOf("presentationml") > -1 ||
    type.indexOf("ppt") > -1 ||
    type.indexOf("pps") > -1
  )
    return <TbPresentation size={20} color="var(--mantine-color-blue-5)" />;
  if (
    type.indexOf("sheet") > -1 ||
    type.indexOf("csv") > -1 ||
    type.indexOf("xls") > -1
  )
    return <TbFileExcel size={20} color="var(--mantine-color-green-5)" />;
  if (type.indexOf("svg") > -1)
    return <TbSvg size={20} color="var(--mantine-color-yellow-5)" />;

  return <TbFile size={20} color="var(--mantine-color-gray-5)" />;
};

interface ChatInputFileProps {
  name: string;
  fileType: string;
  url: string;
  progress: number;
  status: "uploading" | "completed" | "error";
  onRemove: () => void;
}

const ChatInputFile = (props: ChatInputFileProps) => {
  const { name, fileType, url, progress, status, onRemove } = props;

  const { hovered, ref } = useHover();

  const icon = getAttachmentIcon(fileType);

  return (
    <Tooltip
      label={
        <Text maw={200} truncate fz="inherit" c="inherit">
          {name}
        </Text>
      }
    >
      <UnstyledButton
        pos="relative"
        ref={ref}
        className={classes.attachment}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
          URL.revokeObjectURL(url);
        }}
      >
        {status === "completed" ? (
          fileType.startsWith("image/") ? (
            <Image src={url} alt={name} w="100%" h="100%" />
          ) : (
            <Center w="100%" h="100%">
              <ThemeIcon variant="transparent" size="lg">
                {icon}
              </ThemeIcon>
            </Center>
          )
        ) : status === "error" ? (
          <Center w="100%" h="100%">
            <ThemeIcon variant="light" color="red" w={24} h={24}>
              <TbAlertTriangle />
            </ThemeIcon>
          </Center>
        ) : (
          <Center w="100%" h="100%">
            <RingProgress
              size={40}
              thickness={3}
              sections={[
                {
                  value: progress,
                  color: "violet.3"
                }
              ]}
              label={
                <Text fw={400} ta="center" fz={9} c="violet">
                  {numeral(progress / 100).format("0.[00]%")}
                </Text>
              }
            />
          </Center>
        )}

        {hovered && (
          <Overlay color="black" backgroundOpacity={0.5}>
            <Center w="100%" h="100%">
              <ThemeIcon variant="filled" color="red" size={20}>
                <TbX />
              </ThemeIcon>
            </Center>
          </Overlay>
        )}
      </UnstyledButton>
    </Tooltip>
  );
};

export default memo(ChatInputFile);
